import { useEffect, useState } from 'react';

import { DatesProvider as AnyUIDatesProvider, DayOfWeek } from '@any-ui-react/dates';
import dayjs from 'dayjs';
import calendar from 'dayjs/plugin/calendar';
import localeData from 'dayjs/plugin/localeData';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import timezone from 'dayjs/plugin/timezone';
import updateLocale from 'dayjs/plugin/updateLocale';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/ja';
import 'dayjs/locale/ko';
import 'dayjs/locale/zh-cn';

import { useEnvironmentStore } from '../../states';

dayjs.extend(calendar);
dayjs.extend(localeData);
dayjs.extend(localizedFormat);
dayjs.extend(timezone);
dayjs.extend(updateLocale);
dayjs.extend(utc);

export const DatesProvider = ({ children }: { children: React.ReactNode }) => {
  const locale = useEnvironmentStore((state) => state.currentLocale);
  const timezone = useEnvironmentStore((state) => state.currentTimezone);
  const [firstDayOfWeek, setFirstDayOfWeek] = useState<DayOfWeek>();

  useEffect(() => {
    const setupCalendarFormats = (currentLocale: string) => {
      const calendarFormats = {
        lastDay: '[Yesterday at ]HH:mm',
        sameDay: '[Today at ]HH:mm',
        nextDay: '[Tomorrow at ]HH:mm',
        lastWeek: '[last] dddd [at] HH:mm',
        nextWeek: '[next] dddd [at] HH:mm',
        sameElse: 'YYYY/MM/DD HH:mm',
      };

      let localeKey = 'en';

      if (currentLocale === 'ja-JP') {
        calendarFormats.lastDay = '[昨日 ]HH:mm';
        calendarFormats.sameDay = '[今日 ]HH:mm';
        calendarFormats.nextDay = '[明日 ]HH:mm';
        calendarFormats.lastWeek = '[先週] dddd[ ]HH:mm';
        calendarFormats.nextWeek = '[来週] dddd[ ]HH:mm';
        localeKey = 'ja';
      } else if (currentLocale === 'ko-KR') {
        calendarFormats.lastDay = '[어제 ]HH:mm';
        calendarFormats.sameDay = '[오늘 ]HH:mm';
        calendarFormats.nextDay = '[내일 ]HH:mm';
        calendarFormats.lastWeek = '[지난주] dddd[ ]HH:mm';
        calendarFormats.nextWeek = '[다음주] dddd[ ]HH:mm';
        localeKey = 'ko';
      } else if (currentLocale === 'zh-CN') {
        calendarFormats.lastDay = '[昨天 ]HH:mm';
        calendarFormats.sameDay = '[今天 ]HH:mm';
        calendarFormats.nextDay = '[明天 ]HH:mm';
        calendarFormats.lastWeek = '[上周] dddd[ ]HH:mm';
        calendarFormats.nextWeek = '[下周] dddd[ ]HH:mm';
        localeKey = 'zh-cn';
      }

      dayjs.updateLocale(localeKey, { calendar: calendarFormats });
      dayjs.locale(currentLocale);
      const localeData = dayjs().localeData();
      setFirstDayOfWeek(localeData.firstDayOfWeek() as DayOfWeek);
    };

    setupCalendarFormats(locale);
  }, [locale]);

  return (
    <AnyUIDatesProvider
      settings={{
        locale,
        timezone,
        firstDayOfWeek,
      }}
    >
      {children}
    </AnyUIDatesProvider>
  );
};
